import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {
    Autocomplete,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';
import projects from '../../models/projects';
import paymethods from '../../models/paymethods';
import useRootHook from '../../hooks/useRoot';

function routingStateFromProject(project) {
    if (project?.stripe_traffic_paymethod_id) {
        return {mode: 'pm', pmId: project.stripe_traffic_paymethod_id, tags: []};
    }
    if (project?.stripe_traffic_tag) {
        const tags = String(project.stripe_traffic_tag)
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
        return {mode: 'tag', pmId: null, tags};
    }
    return {mode: 'auto', pmId: null, tags: []};
}

function excludedIdsFromProject(project) {
    const raw = project?.stripe_excluded_paymethod_ids;
    if (Array.isArray(raw)) {
        return raw.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n) && n > 0);
    }
    if (typeof raw === 'string' && raw.trim()) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n) && n > 0);
            }
        } catch (_) {}
        return raw.split(',').map((x) => parseInt(x.trim(), 10)).filter((n) => Number.isFinite(n) && n > 0);
    }
    return [];
}

export default function Traffic() {
    const {setShowLoader, notify} = useRootHook();
    const [rows, setRows] = useState(null);
    const [stripeMethods, setStripeMethods] = useState(null);

    const loadTrafficData = async () => {
        setShowLoader(true);
        try {
            const [projectsData, paymethodsData] = await Promise.all([projects.getTraffic(), paymethods.index()]);
            setRows(projectsData || []);

            const stripe = (paymethodsData || [])
                .filter((pm) => pm?.paysystem?.key === 'stripe')
                .filter((pm) => pm?.enabled && pm?.available && !pm?.deleted && !pm?.blocked);

            setStripeMethods(stripe);
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        loadTrafficData();
    }, [setShowLoader]);

    const stripeTags = useMemo(() => {
        const tags = (stripeMethods || [])
            .map((pm) => (pm.stripe_tag || '').trim())
            .filter(Boolean);
        return Array.from(new Set(tags)).sort((a, b) => a.localeCompare(b));
    }, [stripeMethods]);

    const stripeOptions = useMemo(() => {
        return (stripeMethods || []).map((pm) => ({
            id: pm.id,
            label: `${pm.name} (#${pm.id})${pm.stripe_tag ? ` · ${pm.stripe_tag}` : ''}`,
        }));
    }, [stripeMethods]);

    const persistTraffic = async (project, selection, excludedIds) => {
        try {
            setShowLoader(true);
            const payloadExcluded = typeof excludedIds !== 'undefined'
                ? excludedIds
                : undefined;
            await projects.setTraffic(project.id, selection, payloadExcluded);
            setRows((prev) => (prev || []).map((p) => {
                if (p.id !== project.id) return p;
                const next = {...p};
                if (typeof payloadExcluded !== 'undefined') {
                    next.stripe_excluded_paymethod_ids = payloadExcluded;
                }
                if (typeof selection === 'undefined') return next;
                if (!selection || selection === 'auto') {
                    next.stripe_traffic_tag = null;
                    next.stripe_traffic_paymethod_id = null;
                    return next;
                }
                if (selection.startsWith('tag:')) {
                    next.stripe_traffic_tag = selection.slice(4);
                    next.stripe_traffic_paymethod_id = null;
                    return next;
                }
                if (selection.startsWith('pm:')) {
                    next.stripe_traffic_tag = null;
                    next.stripe_traffic_paymethod_id = parseInt(selection.slice(3), 10);
                    return next;
                }
                return next;
            }));
            notify('Traffic settings saved', 1500);
        } finally {
            setShowLoader(false);
        }
    };

    const onModeChange = async (project, mode) => {
        if (mode === 'auto') {
            await persistTraffic(project, 'auto');
            return;
        }
        if (mode === 'pm') {
            const first = stripeMethods?.[0];
            if (first) await persistTraffic(project, `pm:${first.id}`);
            return;
        }
        if (mode === 'tag') {
            const st = routingStateFromProject(project);
            const initial = (st.mode === 'tag' && st.tags.length) ? st.tags : (stripeTags[0] ? [stripeTags[0]] : []);
            if (initial.length) await persistTraffic(project, `tag:${initial.join(',')}`);
        }
    };

    const onTagsChange = async (project, newTags) => {
        const uniq = Array.from(new Set((newTags || []).map((t) => String(t).trim()).filter(Boolean)));
        if (!uniq.length) {
            await persistTraffic(project, 'auto');
            return;
        }
        await persistTraffic(project, `tag:${uniq.join(',')}`);
    };

    const onPmChange = async (project, pmId) => {
        const id = parseInt(pmId, 10);
        if (!Number.isFinite(id)) return;
        await persistTraffic(project, `pm:${id}`);
    };

    const onExcludeChange = async (project, selectedOptions) => {
        const ids = (selectedOptions || []).map((o) => o.id).filter((id) => Number.isFinite(id));
        await persistTraffic(project, undefined, ids);
    };

    if (!rows || !stripeMethods) return null;

    return (
        <React.Fragment>
            <Grid size={12}>
                <PageHeader
                    title="Traffic"
                    subtitle="Route projects to Stripe groups/methods, or exclude specific Stripe MIDs"
                    breadcrumbs={[['Dashboard', '/'], ['Atlas', '/atlas/traffic'], ['Traffic', '/atlas/traffic']]}
                />
            </Grid>

            <Grid size={12}>
                <SectionCard>
                    <TableToolbar
                        title="Active projects"
                        actions={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                    Tags / Specific / Auto + Exclude Stripe MIDs
                                </Typography>
                                <Button size="small" variant="outlined" onClick={loadTrafficData}>
                                    Update
                                </Button>
                            </Stack>
                        }
                    />
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Project</TableCell>
                                <TableCell sx={{minWidth: 360}}>Traffic routing</TableCell>
                                <TableCell sx={{minWidth: 320}}>Exclude Stripes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((p) => {
                                const rs = routingStateFromProject(p);
                                const excludedIds = excludedIdsFromProject(p);
                                const excludedValue = stripeOptions.filter((o) => excludedIds.includes(o.id));
                                return (
                                    <TableRow key={p.id} hover>
                                        <TableCell>
                                            <Stack spacing={0.25}>
                                                <Typography sx={{fontWeight: 700}}>
                                                    {p.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    #{p.id} · {p.uuid}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={1.5}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Mode</InputLabel>
                                                    <Select
                                                        label="Mode"
                                                        value={rs.mode}
                                                        onChange={(e) => onModeChange(p, e.target.value)}
                                                    >
                                                        <MenuItem value="auto">Auto (default)</MenuItem>
                                                        <MenuItem value="tag" disabled={stripeTags.length === 0}>
                                                            Stripe groups (Tag)
                                                        </MenuItem>
                                                        <MenuItem value="pm" disabled={stripeMethods.length === 0}>
                                                            Specific Stripe method
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>

                                                {rs.mode === 'tag' && (
                                                    <Autocomplete
                                                        multiple
                                                        size="small"
                                                        options={stripeTags}
                                                        value={rs.tags.filter((t) => stripeTags.includes(t))}
                                                        onChange={(_, v) => onTagsChange(p, v)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} label="Groups (Tag)" placeholder="ALL, US, …" />
                                                        )}
                                                    />
                                                )}

                                                {rs.mode === 'pm' && (
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Stripe method</InputLabel>
                                                        <Select
                                                            label="Stripe method"
                                                            value={rs.pmId || ''}
                                                            onChange={(e) => onPmChange(p, e.target.value)}
                                                        >
                                                            {stripeMethods.map((pm) => (
                                                                <MenuItem key={`pm-${pm.id}`} value={pm.id}>
                                                                    {pm.name} (#{pm.id}){pm.stripe_tag ? ` · ${pm.stripe_tag}` : ''}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                options={stripeOptions}
                                                value={excludedValue}
                                                onChange={(_, v) => onExcludeChange(p, v)}
                                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                                getOptionLabel={(o) => o.label}
                                                renderTags={(value, getTagProps) =>
                                                    value.map((option, index) => (
                                                        <Chip
                                                            {...getTagProps({index})}
                                                            key={option.id}
                                                            size="small"
                                                            label={option.label}
                                                        />
                                                    ))
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Exclude Stripe MIDs"
                                                        placeholder="Select MIDs…"
                                                    />
                                                )}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </SectionCard>
            </Grid>
        </React.Fragment>
    );
}
